import React from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Phone, Mail, Clock, Facebook, Instagram, Youtube } from 'lucide-react';

const Footer = () => {
    const quickLinks = [
        { name: 'Home', path: '/' },
        { name: 'About Us', path: '/about' },
        { name: 'Admissions', path: '/admissions' },
        { name: 'Academics', path: '/academics' },
        { name: 'Gallery', path: '/gallery' },
        { name: 'Contact Us', path: '/contact' },
    ];

    return (
        <footer className="bg-purple-900 text-white" data-testid="footer">
            <div className="container mx-auto px-4 md:px-8 py-12">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                    {/* About Section */}
                    <div>
                        <img
                            src="https://customer-assets.emergentagent.com/job_rkenglish-dynamic/artifacts/znfm787q_R.K._Eng._High_School_Logo-removebg-preview.png"
                            alt="R.K. English High School"
                            className="h-20 mb-4"
                        />
                        <h3 className="font-outfit font-bold text-white mb-3">R.K. English High School</h3>
                        <p className="text-white text-sm leading-relaxed">
                            A beacon of holistic education, catering to students from K.G. to Junior College. Where Excellence Meets Education.
                        </p>
                    </div>

                    {/* Quick Links */}
                    <div>
                        <h3 className="font-outfit font-bold text-white mb-4">Quick Links</h3>
                        <ul className="space-y-2">
                            {quickLinks.map((link) => (
                                <li key={link.path}>
                                    <Link
                                        to={link.path}
                                        className="text-white hover:text-secondary transition-colors text-sm"
                                        data-testid={`footer-link-${link.name.toLowerCase().replace(' ', '-')}`}
                                    >
                                        {link.name}
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </div>

                    {/* Contact Info */}
                    <div>
                        <h3 className="font-outfit font-bold text-white mb-4">Contact Info</h3>
                        <ul className="space-y-3 text-sm">
                            <li className="flex items-start gap-2 text-white">
                                <MapPin className="w-5 h-5 shrink-0 mt-0.5 text-secondary text-white" />
                                <span>
                                    Alkapuri Station Rd, Saukta Nagar, Achole, Nalasopara East, Maharashtra 401209
                                </span>
                            </li>
                            <li className="flex items-center gap-2 text-white">
                                <Phone className="w-5 h-5 text-secondary text-white" />
                                <a href="tel:09970657588" className="hover:text-secondary transition-colors">
                                    +91 9970657588 |
                                </a>
                                <a href="tel:08390447711" className="hover:text-secondary transition-colors">
                                    +91 8390447711
                                </a>
                            </li>
                            <li className="flex items-center gap-2 text-white">
                                <Mail className="w-5 h-5 text-secondary text-white" />
                                <span>info@rkenglishschool.in</span>
                            </li>
                            <li className="flex items-start gap-2 text-white">
                                <Clock className="w-5 h-5 shrink-0 mt-0.5 text-secondary text-white" />
                                <div>
                                    <div>Mon-Sat: 7:00 AM - 6:00 PM</div>
                                    <div>Sun: 10:00 AM - 2:00 PM</div>
                                </div>
                            </li>
                        </ul>
                    </div>

                    {/* Map & Social */}
                    <div>
                        <h3 className="font-outfit font-bold text-white mb-4">Find Us</h3>
                        <div className="rounded-lg overflow-hidden mb-4 h-40">
                            <iframe
                                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3762.8!2d72.8!3d19.4!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMTnCsDI0JzAwLjAiTiA3MsKwNDgnMDAuMCJF!5e0!3m2!1sen!2sin!4v1234567890"
                                width="100%"
                                height="100%"
                                style={{ border: 0 }}
                                allowFullScreen=""
                                loading="lazy"
                                referrerPolicy="no-referrer-when-downgrade"
                                title="School Location"
                            ></iframe>
                        </div>
                        <div className="flex items-center gap-4">
                            <a
                                href="https://www.facebook.com/p/RK-English-HIGH-School-100063629061408/"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="p-2 bg-red-600 rounded-full hover:bg-secondary transition-colors"
                                aria-label="Facebook"
                            >
                                <Facebook className="w-5 h-5" />
                            </a>
                            <a
                                href="https://instagram.com"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="p-2 bg-red-600 rounded-full hover:bg-secondary transition-colors"
                                aria-label="Instagram"
                            >
                                <Instagram className="w-5 h-5" />
                            </a>
                            <a
                                href="https://youtube.com"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="p-2 bg-red-600 rounded-full hover:bg-secondary transition-colors"
                                aria-label="YouTube"
                            >
                                <Youtube className="w-5 h-5" />
                            </a>
                        </div>
                    </div>
                </div>

                {/* Bottom Bar */}
                <div className="mt-12 pt-8 border-t border-white text-center">
                    <p className="text-white text-sm">
                        © {new Date().getFullYear()} R.K. English High School. All rights reserved. | Designed By <a href="https://wschools.co.in"> WSchools</a>
                    </p>
                </div>
            </div>

            {/* Apply Now CTA */}
            <div className="bg-gradient-to-r from-purple-700 to-purple-900 py-8">
                <div className="container mx-auto px-4 md:px-8 text-center">
                    <h2 className="font-outfit font-bold text-3xl md:text-5xl text-white mb-6">
                        APPLY NOW FOR 2026-2027
                    </h2>
                    <Link
                        to="/admissions"
                        className="inline-flex items-center justify-center px-10 py-4 bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white font-outfit font-bold text-white rounded-full shadow-2xl hover:shadow-red-500/50 transition-all transform hover:scale-105 hover:-tranwhite"
                        data-testid="footer-apply-now-button"
                    >
                        Start Your Journey Today
                    </Link>
                </div>
            </div>
        </footer>
    );
};

export default Footer;